import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip,
  Dialog, Divider, Fab, IconButton, LinearProgress, MenuItem, Paper, Portal, Select, Slide, Stack,
  Tooltip, Typography, useMediaQuery } from '@mui/material';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SendIcon from '@mui/icons-material/Send';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useProjectChat from './useProjectChat';
import useProjectChatDraft from './useProjectChatDraft';
import ProjectChatComposer from './ProjectChatComposer';
import ProjectChatReferenceAnswer from './ProjectChatReferenceAnswer';
import ProjectChatJob, { ProjectChatJobGuidance, ProjectChatJobStatus } from './ProjectChatJob';
import FutureIngestionNotice from './FutureIngestionNotice';
import ProjectChatAgentRun from './ProjectChatAgentRun';
import ProjectChatAnalysis from './ProjectChatAnalysis';
import ProjectChatData from './ProjectChatData';
import ProjectChatSavedAnswer from './ProjectChatSavedAnswer';
import ProjectChatSources from './ProjectChatSources';
import ProjectChatText from './ProjectChatText';
import { PROJECT_CHAT_FAB_BOTTOM } from '../../Utils/projectFloatingActions';

const SideModalTransition = React.forwardRef(function SideModalTransition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} />;
});

const LiveWidgetAnswer = React.lazy(() => import('../Widgets/SingleWidgetAnswerRenderer'));
// Dashboard refreshes can remount the project view. Remember navigation only, never
// messages or approvals; the server reauthorizes every restored conversation.
const rememberedViews = new Map();
const viewStorageKey = identityKey => `project-chat-view:${identityKey}`;
const readRememberedView = identityKey => {
  if (!identityKey) return null;
  if (rememberedViews.has(identityKey)) return rememberedViews.get(identityKey);
  try {
    const saved = JSON.parse(sessionStorage.getItem(viewStorageKey(identityKey)));
    if (saved && typeof saved.open === 'boolean' && /^[a-f0-9]{32}$/.test(saved.conversationId || '')) return saved;
  } catch (_) { /* Navigation still works when browser storage is unavailable. */ }
  return null;
};

const CodeNotice = ({ code, severity = 'info' }) => {
  const { t } = useTranslation();
  return code ? <Alert severity={severity}>{t(`project_chat.codes.${code}`, t('project_chat.codes.needs_review'))}</Alert> : null;
};

export function ResultValues({ results = [], onOpenEvent, focusRef }) {
  const { t } = useTranslation();
  return <Stack ref={focusRef} data-project-chat-step={focusRef ? 'results' : undefined} spacing={1.5}>
    {results.map((result, index) => <Paper key={`${result.event_id}-${index}`} variant="outlined" sx={{ p: 1.5 }}>
      {result.event_id && <Typography variant="caption" color="text.secondary">
        {result.title || t('project_chat.event', { id: result.event_id })}
      </Typography>}
      {result.summary && <Typography variant="body2">{result.summary}</Typography>}
      {result.missing ? <Alert severity="warning">{t('project_chat.missing_value')}</Alert> :
        result.value === 'null' && result.summary ? null :
          <ProjectChatData value={result.value} fields={result.display_fields} widget={Boolean(result.widget_id && !result.data_only)} />}
      {result.saved_values_only && <Alert severity="info">{t('project_chat.job.saved_only')}</Alert>}
      {result.visual_coverage && <Alert severity="info">{t('project_chat.job.visual_coverage', {
        inspected: result.visual_coverage.inspected_pages, total: result.visual_coverage.total_pages
      })}</Alert>}
      {result.source_coverage && <Alert severity="info">{t('project_chat.job.source_coverage')}</Alert>}
      {(result.visual_coverage || result.source_coverage) && result.event_id && onOpenEvent && <Button onClick={() => onOpenEvent(result.event_id)}>
        {t('project_chat.job.recovery.open_event', { id: result.event_id })}
      </Button>}
      {result.calculation && <Typography variant="caption">
        {t(`project_chat.job.calculations.${result.calculation.transform}`, {
          variable: result.calculation.variable
        })}
      </Typography>}
      {result.evidence?.length > 0 && <Accordion elevation={0} disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="caption">{t('project_chat.evidence')}</Typography></AccordionSummary>
        <AccordionDetails>{result.evidence.map((item, evidenceIndex) => <Box key={evidenceIndex} sx={{ mb: 1 }}>
          <Typography variant="caption">{item.widget_id ? `${t('project_chat.targets.widget')} ${item.widget_id}` :
            item.event_id === result.event_id && result.title ? result.title : t('project_chat.event', { id: item.event_id })}</Typography>
          {item.kind === 'visual_observation' && <Typography variant="caption" display="block">
            {t('project_chat.job.visual_observation', { page: item.page })}
          </Typography>}
          <ProjectChatData value={item.quote} />
        </Box>)}</AccordionDetails>
      </Accordion>}
      {result.visual_pages?.length > 0 && <Accordion elevation={0} disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="caption">
          {t('project_chat.job.visual_pages')}
        </Typography></AccordionSummary>
        <AccordionDetails>{result.visual_pages.map(page => <Box key={page.page} sx={{ mb: 1 }}>
          <Typography variant="caption">{t('project_chat.job.visual_observation', { page: page.page })}</Typography>
          {page.quantitative_method && Array.isArray(page.quantitative_items) ? <>
            <Typography variant="body2">{t('project_chat.job.page_result', { value: page.quantitative_result })}</Typography>
            <ProjectChatData value={page.quantitative_items.map(item => page.quantitative_method === 'array_count'
              ? { detail: item.detail } : item)} fields={[
              { key: 'detail', display_label: t('project_chat.job.observed_item') },
              ...(page.quantitative_method === 'sum' ? [{ key: 'number', display_label: t('project_chat.readable.value') }] : [])]} />
          </> : <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{page.observation}</Typography>}
        </Box>)}</AccordionDetails>
      </Accordion>}
      {result.evidence?.length > 0 && <Typography variant="caption" color="text.secondary">
        {t('project_chat.evidence_limit')}
      </Typography>}
    </Paper>)}
  </Stack>;
}

export default function ProjectChat({ caseid, identityKey, disabled = false, onSetup, onOpenEvent, onAvailabilityChange }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const chatLayer = Math.max(theme.zIndex.modal, 2404);
  // Portaled descendants keep this theme, so every nested picker, dialog and
  // tooltip belongs above chat without weakening MUI's focus management.
  const overlayTheme = useMemo(() => createTheme(theme, { zIndex: {
    modal: chatLayer + 1, tooltip: Math.max(theme.zIndex.tooltip, chatLayer + 2),
  } }), [theme, chatLayer]);
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const remembered = readRememberedView(identityKey);
  const [open, setOpen] = useState(Boolean(remembered?.open));
  const [uploading, setUploading] = useState(false);
  const composer = useRef(null);
  const end = useRef(null);
  const currentStep = useRef(null);
  const followLatest = useRef(true);
  const shownConversation = useRef(null);
  const chat = useProjectChat(caseid, open, remembered?.conversationId);
  const conversation = chat.conversation;
  const [draft, setDraft] = useProjectChatDraft(identityKey, caseid, conversation?.id);
  const { text, files } = draft;
  const setText = value => setDraft(current => ({ ...current, text: typeof value === 'function' ? value(current.text) : value }));
  const setFiles = value => setDraft(current => ({ ...current, files: typeof value === 'function' ? value(current.files) : value }));
  const hasFeedback = Boolean(text.trim() || files.length || uploading);
  const state = conversation?.state || {};
  const replyFocused = state.reply_context?.scope === 'reply';
  const trialApproval = state.stage === 'awaiting_trial_approval';
  const preview = !replyFocused && (state.stage === 'awaiting_approval' || trialApproval) ? state.preview : null;
  const busy = chat.pending || Boolean(conversation?.busy);
  const locked = busy || !chat.connected;
  const canAct = action => !state.workflow || state.workflow.actions.includes(action);
  const artifact = preview || (!replyFocused && state.stage === 'awaiting_review' ? state.result : null);
  const lastMessageId = conversation?.messages?.at(-1)?.id;
  const reviewHasResults = !replyFocused && ['awaiting_sample_confirmation', 'awaiting_final_review'].includes(state.job?.phase)
    && !state.job?.result_disputed && state.job?.samples?.some(sample => sample.result);
  const analysisInMessages = conversation?.messages?.some(message => message.content.analysis_job_id === state.job?.id);

  useEffect(() => {
    onAvailabilityChange?.(Boolean(chat.enabled));
    return () => onAvailabilityChange?.(false);
  }, [chat.enabled, onAvailabilityChange]);
  useEffect(() => { if (disabled) setOpen(false); }, [disabled]);

  useEffect(() => {
    if (!identityKey) return;
    const view = { open, conversationId: conversation?.id || remembered?.conversationId };
    rememberedViews.set(identityKey, view);
    try { sessionStorage.setItem(viewStorageKey(identityKey), JSON.stringify(view)); } catch (_) { /* Keep the in-memory view. */ }
    if (rememberedViews.size > 50) rememberedViews.delete(rememberedViews.keys().next().value);
  }, [identityKey, open, conversation?.id, remembered?.conversationId]);

  const scrollCurrentStep = useCallback(() => {
    const target = currentStep.current || end.current;
    target?.scrollIntoView?.({ behavior: 'auto', block: currentStep.current ? 'start' : 'end' });
  }, []);
  const showCurrentStep = () => { followLatest.current = true; scrollCurrentStep(); };
  const openWidgetSettings = variable => {
    setOpen(false);
    navigate(`/case/${encodeURIComponent(caseid)}?openWidgetSettings=true&widgetTag=${encodeURIComponent(variable)}`);
  };
  useEffect(() => {
    if (shownConversation.current !== conversation?.id) {
      shownConversation.current = conversation?.id;
      followLatest.current = true;
    }
    if (open && followLatest.current) {
      if (busy || state.workflow?.next_step === 'processing') end.current?.scrollIntoView?.({ behavior: 'auto', block: 'end' });
      else scrollCurrentStep();
    }
  }, [open, conversation?.id, lastMessageId, state.stage, state.job?.phase, state.workflow?.next_step, busy, scrollCurrentStep]);
  useEffect(() => {
    if (chat.rejectedMessage && chat.rejectedMessage.conversationId === conversation?.id) {
      setDraft(current => current.text ? current : { text: chat.rejectedMessage.text,
        files: chat.rejectedMessage.references?.files || current.files });
    }
  }, [chat.rejectedMessage, conversation?.id, setDraft]);
  const submit = () => {
    if (!text.trim() || text.length > 12000 || locked || uploading || !conversation || composer.current?.hasPendingAttachments()) return;
    followLatest.current = true;
    chat.act('message', { text: text.trim(), references: composer.current?.getReferences() || { files: [], tags: [] } });
    setDraft({ text: '', files: [] });
  };

  if (!chat.enabled) return null;
  return <>
    <Portal><Tooltip title={t('project_chat.title')} arrow placement="left">
      <Box component="span" sx={{ position: 'fixed', bottom: PROJECT_CHAT_FAB_BOTTOM,
        right: { xs: 16, sm: 24 }, display: 'inline-flex', zIndex: 1000 }}>
        <Fab data-project-chat-launcher color="primary" onClick={() => setOpen(true)} disabled={disabled}
        aria-label={t('project_chat.title')} aria-haspopup="dialog" aria-expanded={open && !disabled}
        sx={{ width: { xs: 48, sm: 56 }, height: { xs: 48, sm: 56 },
          bgcolor: '#5757b0', color: '#fff', boxShadow: '0 18px 36px rgba(87, 87, 176, 0.35)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': { bgcolor: '#7777d0', transform: 'translateY(-2px)',
            boxShadow: '0 22px 44px rgba(87, 87, 176, 0.45)' } }}>
        <ChatBubbleOutlineIcon />
        </Fab>
      </Box>
    </Tooltip></Portal>
    <ThemeProvider theme={overlayTheme}>
    <Dialog keepMounted open={open && !disabled} onClose={() => setOpen(false)}
      maxWidth={false} TransitionComponent={SideModalTransition}
      sx={{ zIndex: chatLayer, '& .MuiDialog-container': { justifyContent: 'flex-end' } }}
      TransitionProps={{ onEntered: () => { if (followLatest.current) showCurrentStep(); } }}
      PaperProps={{ 'aria-label': t('project_chat.title'), 'aria-modal': true, sx: { width: mobile ? 'calc(100% - 16px)' : 480,
        maxWidth: mobile ? 'calc(100% - 16px)' : 'calc(100% - 32px)',
        height: mobile ? 'calc(100% - 16px)' : 'calc(100% - 32px)', maxHeight: 'none',
        m: mobile ? 1 : 2, borderRadius: 3, display: 'flex', p: 0, overflow: 'hidden' } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2 }}>
        <Box><Typography variant="h6">{t('project_chat.title')}</Typography>
          <Typography variant="caption" color="text.secondary">{t('project_chat.private')}</Typography></Box>
        <IconButton onClick={() => setOpen(false)} aria-label={t('project_chat.close')}><CloseIcon /></IconButton>
      </Stack>
      <Stack direction="row" spacing={1} sx={{ px: 2, pb: 1, minWidth: 0 }}>
        <Select size="small" displayEmpty fullWidth value={chat.history.some((item) => item.id === conversation?.id) ? conversation.id : ''}
          onChange={(event) => chat.choose(event.target.value)} aria-label={t('project_chat.history')} disabled={!chat.connected}
          sx={{ minWidth: 0, '& .MuiSelect-select': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
          MenuProps={{ sx: { zIndex: chatLayer + 1 },
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'left' },
            PaperProps: { sx: { maxHeight: 320, maxWidth: 'min(428px, calc(100vw - 32px))' } } }}>
          <MenuItem value="" disabled>{t('project_chat.history')}</MenuItem>
          {chat.history.map((item) => <MenuItem key={item.id} value={item.id} sx={{ minWidth: 0 }}>
            <Typography noWrap variant="body2" title={item.title || t('project_chat.untitled')}>
              {item.title || t('project_chat.untitled')}
            </Typography>
          </MenuItem>)}
        </Select>
        <Button onClick={chat.newConversation} disabled={!chat.connected || (!conversation && chat.pending)} sx={{ flexShrink: 0 }}>{t('project_chat.new')}</Button>
      </Stack>
      <Divider />
      <Box data-project-chat-scroll sx={{ flex: 1, overflowY: 'auto', p: 2 }}
        onWheel={() => { followLatest.current = false; }}
        onTouchMove={() => { followLatest.current = false; }}
        onPointerDown={event => { if (event.target === event.currentTarget) followLatest.current = false; }}
        onKeyDown={event => {
          if (['PageUp', 'PageDown', 'Home', 'End', 'ArrowUp', 'ArrowDown'].includes(event.key)) followLatest.current = false;
        }} onScroll={event => {
        const element = event.currentTarget;
        // Scrolling to the start of a long reply is intentional. Its native
        // scroll event must not be mistaken for the user leaving live updates.
        if (element.scrollHeight - element.scrollTop - element.clientHeight < 64) followLatest.current = true;
      }}>
        <Stack spacing={2}>
          {!chat.connected && <CodeNotice code="connection" severity="warning" />}
          <CodeNotice code={chat.error} severity="error" />
          <ProjectChatAgentRun run={state.agent_run} locked={locked} working={busy}
            hasFeedback={hasFeedback} onRetry={message => chat.act('message', { text: message })} />
          {state.blocking_objects?.length > 0 && <Alert severity="warning">
            {t('project_chat.blocking_objects')}
            {state.blocking_objects.map(item => <Typography key={`${item.target}-${item.id}`} variant="body2">
              {t(`project_chat.blocker_${item.target}`, { id: item.id })}
            </Typography>)}
          </Alert>}
          {(!replyFocused && ['needs_review', 'blocked', 'cancelled'].includes(state.stage)) && <Typography variant="caption">
            {t('project_chat.recovery_navigation')}
          </Typography>}
          {(chat.error === 'protocol_mismatch' || !chat.connected || chat.error === 'connection') && <Button component="a" href={window.location.href}>
            {t('appRecovery.reload')}
          </Button>}
          {conversation?.has_older && <Button onClick={chat.loadOlder} disabled={chat.pending}>{t('project_chat.older')}</Button>}
          {!conversation?.messages?.length && <Box sx={{ py: 3 }}>
            <Typography variant="h6" gutterBottom>{t('project_chat.welcome')}</Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>{t('project_chat.intro')}</Typography>
            <Stack spacing={1}>{['suggest_goal', 'suggest_tags', 'suggest_data'].map((key) =>
              <Button key={key} variant="outlined" onClick={() => { setText(t(`project_chat.${key}`)); composer.current?.focus(); }}
                sx={{ textTransform: 'none', justifyContent: 'flex-start' }}>{t(`project_chat.${key}`)}</Button>)}</Stack>
          </Box>}
          {state.goal && !replyFocused && <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
            <Typography variant="caption" color="text.secondary">{t('project_chat.goal')}</Typography>
            <Typography variant="body2">{state.goal}</Typography>
            {state.steps?.length > 0 && <Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="caption">{t('project_chat.plan')}</Typography></AccordionSummary>
              <AccordionDetails><Box component="ol" sx={{ pl: 2, m: 0 }}>{state.steps.map((step, index) =>
                <Typography component="li" key={index} variant="body2" sx={{ mb: 1 }}>{step}</Typography>)}</Box></AccordionDetails>
            </Accordion>}
          </Paper>}
          {conversation?.messages?.map((message) => <Box key={message.id}
            ref={message.id === lastMessageId && message.role === 'assistant' ? currentStep : undefined}
            data-project-chat-step={message.id === lastMessageId && message.role === 'assistant' ? 'message' : undefined}
            sx={{ alignSelf: message.role === 'user' ? 'flex-end' : 'stretch', maxWidth: '100%' }}>
            {message.content.text && <Paper elevation={0} sx={{ p: 1.5, bgcolor: message.role === 'user' ? 'action.selected' : 'transparent' }}>
              {message.role === 'assistant' ? <ProjectChatText text={message.content.text} />
                : <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{message.content.text}</Typography>}
            </Paper>}
            <CodeNotice code={message.content.code === 'result_disputed' && message.content.text ? null : message.content.code} />
            {message.content.references && <Stack direction="row" useFlexGap flexWrap="wrap" spacing={0.5}>
              {message.content.references.files.map(file => <Chip size="small" key={file.reference_id} label={file.title} />)}
              {message.content.references.tags.map(tag => <Chip size="small" key={tag} label={`{${tag}}`} />)}
            </Stack>}
            {message.content.reference_answer && <ProjectChatReferenceAnswer answer={message.content.reference_answer} />}
            <ProjectChatSources sources={message.content.source_links} inline connected={chat.connected}
              onOpenEvent={onOpenEvent ? id => { setOpen(false); onOpenEvent(id); } : undefined} />
            {message.content.answer && <ProjectChatSavedAnswer answer={message.content.answer}
              locked={locked} onCheck={message.id === lastMessageId ? question => chat.act('message', { text: question }) : undefined} />}
            {message.content.analysis_job && <ProjectChatAnalysis job={message.content.analysis_job}
              locked={locked} onRequestCorrection={!replyFocused && message.content.analysis_job_id === state.job?.id && message.id === lastMessageId
                ? question => chat.act('message', { text: question }) : undefined}
              connected={chat.connected} onOpenEvent={onOpenEvent ? id => { setOpen(false); onOpenEvent(id); } : undefined} />}
            {message.content.job_status && <ProjectChatJobStatus status={message.content.job_status} />}
            {message.content.job_guidance && <Paper variant="outlined" sx={{ p: 1.5 }}>
              <ProjectChatJobGuidance guidance={message.content.job_guidance} />
            </Paper>}
          </Box>)}
          {state.answer && !conversation?.messages?.some(message => message.content.answer) &&
            <ProjectChatSavedAnswer answer={state.answer} locked={locked} onCheck={question => chat.act('message', { text: question })} />}
          {artifact && <Paper ref={currentStep} data-project-chat-step="preview" variant="outlined" sx={{ p: 2, borderColor: 'primary.light' }}>
            <Stack spacing={1.5}>
              <Typography variant="subtitle1">{t(preview ? 'project_chat.preview' : 'project_chat.result')}</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip size="small" label={t(`project_chat.targets.${artifact.operation.target}`)} />
                <Chip size="small" label={t(`project_chat.operations.${artifact.operation.operation}`)} />
              </Stack>
              <Typography variant="body2">{artifact.operation.title || artifact.operation.variable}</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{artifact.operation.instructions}</Typography>
              <Typography variant="caption">{t('project_chat.criteria')}: {artifact.operation.success_criteria}</Typography>
              <FutureIngestionNotice proposal={artifact.future_ingestion} futureOnly={artifact.operation.target === 'ingestion_tag'} />
              {preview && <Typography variant="body2">{t('project_chat.targets_count', { count: preview.target_count })}</Typography>}
              {preview?.previous_saved > 0 && <Alert severity="info">
                {t('project_chat.job.recovery.saved', { saved: preview.previous_saved, remaining: preview.target_count })}
              </Alert>}
              {artifact.operation.event_ids?.length > 0 && <Accordion disableGutters elevation={0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="body2">{t('project_chat.affected_events')}</Typography></AccordionSummary>
                <AccordionDetails><Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{artifact.operation.event_ids.join(', ')}</Typography></AccordionDetails>
              </Accordion>}
              {preview?.before?.length > 0 && <Accordion disableGutters elevation={0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="body2">{t('project_chat.before')}</Typography></AccordionSummary>
                <AccordionDetails><ResultValues results={preview.before} /></AccordionDetails>
              </Accordion>}
              {artifact.sample && <Alert severity="info">{t(trialApproval ? preview.decision?.start_hint_key || 'project_chat.job.trial_hint' : artifact.operation.target === 'event_rule' ? 'project_chat.future_only' : 'project_chat.sample', {
                total: preview?.target_count })}</Alert>}
              {trialApproval && preview.decision?.start_key !== 'project_chat.outcome.run_private' && <Typography variant="body2">{t('project_chat.job.sample_events', { ids: preview.sample_ids.join(', ') })}</Typography>}
              {trialApproval && <Alert severity={preview.result_scope === 'original_sources' ? 'info' : 'warning'}>
                {t(`project_chat.job.scope.${preview.result_scope || 'unverified'}`)}
              </Alert>}
              {artifact.operation.target === 'event_rule' && !artifact.rule_enabled && artifact.operation.operation !== 'remove' &&
                <Alert severity="info">{t('project_chat.rule_paused')}</Alert>}
              <ResultValues results={artifact.results} />
              {state.live_widget?.response && <Box sx={{ height: 320, overflow: 'auto' }}>
                <Typography variant="subtitle2">{t('project_chat.live_value')}</Typography>
                <Suspense fallback={<LinearProgress />}>
                  <LiveWidgetAnswer answer={state.live_widget.response} widgetId={state.live_widget.widget_id}
                    isMaximized={false} shouldAutoExpand={false} t={t} />
                </Suspense>
              </Box>}
              {state.live_widget?.partial && <CodeNotice code="large_result" />}
              {state.live_widget && <Button variant="outlined" onClick={() => openWidgetSettings(state.live_widget.variable)}>
                {t('project_chat.open_widget')}</Button>}
              {preview?.downstream_actions?.length > 0 && <Alert severity="warning">
                {t('project_chat.downstream', { count: preview.downstream_actions.length })}
              </Alert>}
              {(state.stage === 'awaiting_approval' || trialApproval) && <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={() => chat.act('approve')} disabled={locked || hasFeedback || !canAct('approve')}>{t(trialApproval ? preview.decision?.start_key || 'project_chat.job.run_trial' : 'project_chat.apply')}</Button>
                <Button onClick={() => composer.current?.focus()} disabled={locked}>{t('project_chat.change')}</Button>
                <Button onClick={() => chat.act('cancel')} disabled={locked}>{t('project_chat.cancel')}</Button>
              </Stack>}
              {state.stage === 'awaiting_review' && <>
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" onClick={() => chat.act('confirm')} disabled={locked || hasFeedback || !canAct('confirm')}>{t('project_chat.confirm')}</Button>
                  <Button onClick={() => composer.current?.focus()} disabled={locked}>{t('project_chat.adjust')}</Button>
                </Stack>
              </>}
            </Stack>
          </Paper>}
          {!replyFocused && state.job_revision_requested && !state.job_issue && <CodeNotice code="plan_revision" />}
          {state.workflow?.has_background_work && <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="body2">{t(state.job?.result_disputed ? 'project_chat.readable.unresolved' : 'project_chat.readable.background_work')}</Typography>
            <Button disabled={locked} onClick={() => chat.act('resume_task')}>{t('project_chat.readable.return_to_work')}</Button>
          </Paper>}
          {!replyFocused && <>
          <Box ref={state.job?.analysis_result && !analysisInMessages ? currentStep : undefined} data-project-chat-step="answer">
          <ProjectChatAnalysis job={analysisInMessages ? null : state.job} connected={chat.connected}
            locked={locked} onRequestCorrection={question => chat.act('message', { text: question })}
            onOpenEvent={onOpenEvent ? id => { setOpen(false); onOpenEvent(id); } : undefined} />
          </Box>
          <Box ref={!reviewHasResults && state.job && (!['completed', 'draft'].includes(state.job.phase) || state.job.result_disputed) ? currentStep : undefined}
            data-project-chat-step="job">
          <ProjectChatJob job={state.job} locked={locked} connected={chat.connected}
            hasFeedback={hasFeedback || Boolean(state.job_revision_requested)} canConfirm={canAct('confirm')}
            onConfirm={() => chat.act('confirm')} onCancel={() => chat.act('cancel')}
            onInvestigate={() => chat.act('message', { text: t('project_chat.readable.investigate_request') })}
            onAdjust={() => composer.current?.focus()} renderValues={results => <ResultValues results={results} focusRef={reviewHasResults ? currentStep : undefined}
              onOpenEvent={onOpenEvent ? id => { setOpen(false); onOpenEvent(id); } : undefined} />}
            recheckStatus={chat.recheckStatus} onRecheck={chat.refresh} onPrepareRemaining={() => chat.act('prepare_remaining')}
            onPrepareRetry={canAct('prepare_retry') ? () => chat.act('prepare_retry') : undefined}
            onRequestRevision={canAct('prepare_revision') ? () => chat.act('prepare_revision') : undefined}
            onOpenEvent={onOpenEvent ? id => { setOpen(false); onOpenEvent(id); } : undefined}
            onOpenWidget={() => openWidgetSettings(state.job?.variable || '')} />
          </Box>
          </>}
          {!(state.job?.result_disputed && (state.reason || state.code) === 'result_disputed') &&
            <CodeNotice code={state.reason || state.code} severity={['blocked', 'result_disputed'].includes(state.stage) ? 'warning' : 'info'} />}
          {(state.reply_context ? state.reply_context.scope === 'reply' && state.reply_context.kind === 'setup' : state.stage === 'setup') &&
            <Button variant="contained" disabled={locked || !chat.connected} onClick={() => { setOpen(false); onSetup?.(); }}>{t('project_chat.setup')}</Button>}
          {!replyFocused && (state.stage === 'needs_review' || state.stage === 'cancelled') && state.receipts?.length > 0 && <Alert severity="warning">
            {t('project_chat.completed_effects', { count: state.receipts.length })}
            {state.receipts.map((receipt, index) => <Typography key={index} variant="body2">
              {receipt.event_id ? t('project_chat.event', { id: receipt.event_id }) :
                `${t(`project_chat.targets.${receipt.target === 'widget_result' ? 'widget' : receipt.target}`)} ${receipt.widget_id || receipt.action_id || ''}`}
              {receipt.variable ? ` · ${receipt.variable}` : ''}
            </Typography>)}
          </Alert>}
          {!replyFocused && ['needs_review', 'cancelled', 'blocked'].includes(state.stage) && state.uncertain_operation && <Alert severity="warning">
            {t('project_chat.uncertain')}
            <Typography variant="body2">{state.uncertain_operation.variable}
              {` · ${state.uncertain_operation.event_id || state.uncertain_operation.widget_id || state.uncertain_operation.action_id || ''}`}</Typography>
          </Alert>}
          {busy && <Box role="status" aria-live="polite">
            <Typography variant="body2" sx={{ mb: 1 }}>{t(`project_chat.progress.${chat.progress?.code || 'working'}`)}</Typography>
            <LinearProgress variant={chat.progress?.total > 0 ? 'determinate' : 'indeterminate'}
              value={chat.progress?.total > 0 ? Math.min(100, 100 * (chat.progress.current || 0) / chat.progress.total) : undefined} />
            {chat.progress?.total > 0 && <Typography variant="caption">{chat.progress.current || 0} / {chat.progress.total}</Typography>}
            <Button onClick={() => chat.act('cancel')} disabled={!chat.connected || conversation?.cancel_requested}>{t('project_chat.stop')}</Button>
            {conversation?.busy && Date.now() / 1000 - conversation.updated_at > 1200 &&
              <Button onClick={() => chat.act('recover')}>{t('project_chat.recover')}</Button>}
          </Box>}
          <div ref={end} />
        </Stack>
      </Box>
      <Divider />
      <Box component="form" onSubmit={(event) => { event.preventDefault(); submit(); }} sx={{ p: 2 }}>
        {state.workflow && !['answer', 'clarify', 'complete', 'setup', 'processing'].includes(state.workflow.next_step) && <Alert severity="info" icon={false} sx={{ mb: 1, py: 0.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography variant="subtitle2">{t('project_chat.next_step')}</Typography>
            {state.workflow.next_step !== 'clarify' && <Button size="small" onClick={showCurrentStep}>
              {t('project_chat.current_step')}
            </Button>}
          </Stack>
          <Typography variant="body2">{t(`project_chat.next_steps.${state.workflow.next_step}`)}</Typography>
        </Alert>}
        <Stack direction="row" spacing={1} alignItems="flex-end">
          <ProjectChatComposer key={`${identityKey}:${caseid}:${conversation?.id}`} ref={composer}
            text={text} files={files} onText={setText} onFiles={setFiles} onSubmit={submit}
            onPendingChange={setUploading} tags={chat.referenceCatalog?.tags} hasMoreTags={chat.referenceCatalog?.has_more}
            onMoreTags={chat.loadMoreTags} caseid={caseid} active={open && !disabled} disabled={locked || !conversation} />
          <IconButton type="submit" color="primary" disabled={locked || uploading || !text.trim() || text.length > 12000 || !conversation} aria-label={t('project_chat.send')}><SendIcon /></IconButton>
        </Stack>
        {text.length > 12000 && <CodeNotice code="request_too_long" severity="warning" />}
      </Box>
    </Dialog>
    </ThemeProvider>
  </>;
}
